import { Adapter, WalletReadyState } from '@solana/wallet-adapter-base'

/**
 * What a given wallet can actually do in *this* browser, right now.
 *
 * Every wallet picker on the site used to answer these questions for itself, and they disagreed:
 * the deposit picker filtered nothing and offered wallets that could not be reached, the account
 * picker asked the player to self-declare a Ledger via a checkbox. Both are properties the adapter
 * already knows. Ask it here, in one place.
 */

export const SYSTEM_PROGRAM_ID = '11111111111111111111111111111111'

/**
 * `Installed` means the extension is present. `Loadable` means the wallet works without one --
 * a hardware transport, or a hosted wallet that opens in a popup. Anything else (`NotDetected`,
 * `Unsupported`) cannot be reached from this browser, so offering it only produces a click that
 * fails with no explanation.
 */
export const isReachable = (adapter: Adapter) =>
    adapter.readyState === WalletReadyState.Installed || adapter.readyState === WalletReadyState.Loadable

/**
 * Wallet Standard feature names. Spelled out rather than imported from
 * `@solana/wallet-standard-features`, which is a transitive dependency this app does not declare.
 * They are a stable part of the spec.
 */
const SOLANA_SIGN_MESSAGE = 'solana:signMessage'
const SOLANA_SIGN_TRANSACTION = 'solana:signTransaction'

/**
 * A wallet that registers itself through the Wallet Standard does not reach us as the class
 * adapter constructed in `WalletContext` -- `useStandardWalletAdapters` wraps the registration in a
 * `StandardWalletAdapter` and then discards the hand-registered adapter of the same name. Phantom,
 * Solflare and Backpack all take that path, so at runtime they are `StandardWalletAdapter`s.
 *
 * That class keeps no `signMessage`/`signTransaction` on its prototype. It *assigns* them to the
 * instance in a private `reset()`, guarded on the **connected account** advertising the feature,
 * and its constructor calls `reset()` while the account is still null -- so construction runs
 * `delete this.signTransaction`. `'signTransaction' in adapter` is therefore false for every such
 * wallet until it is already connected.
 *
 * A picker that filters on that deadlocks: the wallet is hidden because it is not connected, and
 * it cannot be connected because it is hidden. Ask the underlying wallet what it advertises
 * instead, which is answerable before connect. Class adapters expose no `wallet`, so they keep the
 * method check -- their methods live on the prototype and are always present.
 */
const supports = (adapter: Adapter, method: 'signMessage' | 'signTransaction', feature: string) => {
    const features = (adapter as Adapter & { wallet?: { features?: Record<string, unknown> } }).wallet
        ?.features

    return features ? feature in features : method in adapter
}

/**
 * Ledger implements `signTransaction` but **not** `signMessage` -- it extends
 * `BaseSignerWalletAdapter` rather than `BaseMessageSignerWalletAdapter`. `useWallet()` mirrors
 * that, handing back `signMessage: undefined`. Any flow that assumes the method exists throws a
 * TypeError the instant a Ledger is selected, which is why linking one never worked unless the
 * player happened to tick a checkbox first.
 */
export const canSignMessage = (adapter: Adapter) => supports(adapter, 'signMessage', SOLANA_SIGN_MESSAGE)

export const canSignTransaction = (adapter: Adapter) =>
    supports(adapter, 'signTransaction', SOLANA_SIGN_TRANSACTION)

/**
 * Wallets that verify ownership by signing a throwaway transaction instead of a message. Derived,
 * never asked: it covers Ledger and any other hardware wallet added later without a second flag to
 * remember. The backend stores this as `Wallet.is_ledger`.
 */
export const usesTransactionProof = (adapter: Adapter) => !canSignMessage(adapter) && canSignTransaction(adapter)

/** A wallet we cannot link at all -- it can produce neither proof. */
export const canLink = (adapter: Adapter) => canSignMessage(adapter) || canSignTransaction(adapter)
